import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ColumnComponent } from "@progress/kendo-angular-grid";
import { TableSettingsColumn } from "../models/table-settings-column";
import { AppServiceHelperService } from "./app-service-helper.service";
import { take } from "rxjs";

/**
 * Table settings service
 */
@Injectable({
  providedIn: "root"
})
export class TableSettingsService {
  /**
   * Constructor
   * @param http HttpClient
   * @param appServiceHelper AppServiceHelperService
   */
  constructor(private http: HttpClient, private appServiceHelper: AppServiceHelperService) {
    //Nothing
  }

  /**
   * Indicates whether the data are already loaded
   */
  private loaded = {};

  /**
   * Prevents from double-loading when called while loading
   */
  private loadStartedPromise = {};

  /**
   * Has new columns?
   */
  private hasNewColumns = {};

  /**
   * Table settings data
   */
  private tableSettingsData = {};

  /**
   * Loads area table settings
   * @param area The area
   * @returns
   */
  private loadAreaTableSettings(area: string): Promise<TableSettingsColumn[]> {
    const url = this.appServiceHelper.getServiceEndpoint('pollux.api', `TableSettings/details/single?area=${area}`);
    return new Promise<any>((resolve) => {
      return this.http.get<TableSettingsColumn[]>(url).pipe(take(1)).subscribe((result) => {
        resolve(result);
      });
    });
  }

  /**
   * Saves area table settings
   * @param area
   * @param areaData
   * @returns
   */
  private saveAreaTableSettings(area: string, areaData: any): Promise<any> {
    const postObject = {};
    postObject[area] = areaData;
    const url = this.appServiceHelper.getServiceEndpoint('pollux.api', `TableSettings/details?area=${area}`);
    return new Promise<any>((resolve) => {
      this.http.post<any>(url, postObject).pipe(take(1)).subscribe((result) => {
        resolve(result);
      });
    })
  }

  /**
   * Loads the table settings for the current user
   * @param area
   * @returns
   */
  public load(area: string): Promise<TableSettingsColumn[]> {
    if (this.loaded[area]) {
      //Already loaded - return immediately
      return Promise.resolve(this.tableSettingsData[area]);
    }

    if (this.loadStartedPromise[area]) {
      //Loading is already in progress - return loading promise
      return this.loadStartedPromise[area];
    }

    this.loadStartedPromise[area] = new Promise<any>((resolve) => {
      this.loadAreaTableSettings(area).then((gridSettings: TableSettingsColumn[]) => {
        this.loaded[area] = true;
        this.tableSettingsData[area] = gridSettings;
        resolve(gridSettings);
      });
    });

    return this.loadStartedPromise[area];
  }

  /**
    * Saves the table settings for the current user. Called if any column was moved, resized, shown or hidden.
    * 
    * @param area The source area
    * @param modifiedColumn The column that was modified
    * @param gridColumns The grid columns array
    */
  async updateGrid(area, modifiedColumn, gridColumns: ColumnComponent[]) {
    if (modifiedColumn.field && this.tableSettingsData[area]) {
      const areaData = this.tableSettingsData[area] as TableSettingsColumn[];

      for (const gridColumn of gridColumns) {
        const column: TableSettingsColumn = areaData.find(element => element.columnName === gridColumn.field);
        if (column) {
          column.index = gridColumn.orderIndex;
          column.width = gridColumn.width;
          column.show = !gridColumn.hidden;
        } else {
          //column does not exists in area still
          areaData.push({
            areaName: area,
            columnName: gridColumn.field,
            index: gridColumn.orderIndex,
            oid: -1,
            show: !gridColumn.hidden,
            width: gridColumn.width
          });
        }
      }

      areaData.sort(this.sortByIndexAscComparer);
      areaData.forEach((column, i) => {
        column.index = i;
      });

      try {
        await this.saveAreaTableSettings(area, areaData);
        if (this.hasNewColumns[area]) {
          //reload only if there is some new column
          const gridSettings: TableSettingsColumn[] = await this.loadAreaTableSettings(area);
          this.tableSettingsData[area] = gridSettings;
          this.hasNewColumns[area] = false;
          return true;
        }
      } catch {
        //do nothing (for now)
        return false;
      }

      return true;
    }
  }

  /**
    * Applies the loaded table settings to a grid
    * 
    * @param area The desired area
    * @param gridColumns The columns that are present in the current grid. Please note that each element must have an 'orderIndex' property.
    */
  processGrid(area: string, gridColumns: ColumnComponent[]) {
    let areaData;

    if (this.tableSettingsData[area]) {
      areaData = this.loadGridData(area, gridColumns);
    }
    else {
      //Create
      areaData = this.createGridData(area, gridColumns);
    }

    areaData.sort(this.sortByIndexAscComparer);
  }

  /**
   * Creates the grid data
   * @param area
   * @param gridColumns
   * @returns
   */
  private createGridData(area: string, gridColumns: ColumnComponent[]) {
    this.tableSettingsData[area] = [];
    const areaData = this.tableSettingsData[area];
    for (const gridColumn of gridColumns) {
      if (gridColumn.field) {
        areaData.push({
          index: gridColumn.orderIndex,
          columnName: gridColumn.field,
          width: gridColumn.width,
          show: !gridColumn.hidden,
          oid: -1,
          areaName: area
        });

        if (typeof (gridColumn.hidden) === "undefined") {
          gridColumn.hidden = false;
        }

        this.hasNewColumns[area] = true;
      }
    }
    return areaData;
  }

  /**
   * Loads the grid data
   * @param area
   * @param gridColumns
   * @returns
   */
  private loadGridData(area: string, gridColumns: ColumnComponent[]): any {
    let areaData = this.tableSettingsData[area];
    //Load
    for (const gridColumn of gridColumns) {
      if (gridColumn.field) {
        const columnSettings = areaData.find(element => element.columnName === gridColumn.field);

        if (columnSettings) {
          gridColumn.width = columnSettings.width;
          gridColumn.hidden = !columnSettings.show;
          gridColumn.orderIndex = columnSettings.index;
        }
        else {
          //New field
          areaData.push({
            index: gridColumn.orderIndex,
            columnName: gridColumn.field,
            width: gridColumn.width,
            show: !gridColumn.hidden,
            oid: -1,
            areaName: area
          });

          if (typeof (gridColumn.hidden) === "undefined") {
            gridColumn.hidden = false;
          }

          this.hasNewColumns[area] = true;
        }
      }
    }

    //remove columns that no longer exists from areaData
    const columnNamesArray = gridColumns.filter((obj) => obj.field).map((obj) => obj.field);
    areaData = areaData.filter((obj) => columnNamesArray.indexOf(obj.columnName) !== -1);
    return areaData;
  }

  /**
   * Sort by index field (ascending)
   * @param a
   * @param b
   * @returns
   */
  private sortByIndexAscComparer(a, b) {
    if (a.index < b.index) {
      return -1;
    }
    if (a.index > b.index) {
      return 1;
    }

    return 0;
  }
}
